import CardCommon from '@/pages/opinion/components/CardCommon';
import React, { useEffect, useMemo, useState } from 'react';
import { Col } from 'tea-component';
import BarAndLineChart from '../ChartComponent/BarAndLineChart';
import CoordinateChart from '../../../Performance/components/ChannelsLanguageChart/CoordinateChart';
import BarChart from '../ChartComponent/BarChart';
import LineChart from '../ChartComponent/LineChart';
import { arrayToSimpleObj, getPercentageValue } from '@/pages/opinion/utils';
import { getCompetitorKeyMetricsList } from '@/pages/opinion/services';

import useLanguage from '@/pages/opinion/hooks/useLanguage';
import {
  barIcon,
  coordinateIcon,
  lineIcon,
} from '@/pages/opinion/constants/chart';

export default function TopMentionsSentiment({
  barDataMap,
  lineDataMap,
  loading,
  commonParams,
  xAxisList,
}: any) {
  const [chartOperation, setChartOperation] = useState<string>('bar');
  const [filterValue, setFilterValue] = useState('account_posts');
  const [postsOperation, setPostsOperation] = useState<string>('line');

  const chartData = useMemo(() => {
    const dataInfo: any = postsOperation === 'bar' ? barDataMap : lineDataMap;

    return dataInfo[filterValue];
  }, [barDataMap, lineDataMap, filterValue, postsOperation]);
  const { formatMessage, formatOptionsMessage } = useLanguage();
  const [mentionsLoading, setMentionsLoading] = useState(true);

  // 堆积图数据
  const [mentionsList, setMentionsList] = useState<any>([]);

  // 矩阵图数据
  const [scatterChartList, setScatterChartList] = useState<any>([]);

  const getKeyMetricsData = async () => {
    try {
      setMentionsLoading(true);
      const res = await getCompetitorKeyMetricsList({
        ...commonParams,
        metrics: ['mentions', 'avg_sentiment', 'sentiment'],
        need_link: false,
      });

      let data = res || [];
      let stacked: any = {};
      let matrixDiagram: Array<any> = [];
      const keys = ['Sentiment', 'Positive', 'Neutral', 'Negative'];
      const keyMap: any = {
        Sentiment: 'avg_sentiment',
        Positive: 'positive_mentions',
        Neutral: 'neutral_mentions',
        Negative: 'negative_mentions',
      };

      data.forEach((item: any) => {
        let { game, value } = item || {};
        let { new_list } = value || {};
        const infoMap = arrayToSimpleObj(new_list, 'value', 'key');

        keys.forEach((key) => {
          if (!stacked[key]) {
            stacked[key] = {
              name: key,
              games: [],
            };
          }
          let infoKey = keyMap[key];
          let value = infoMap[infoKey];
          let rate =
            key === 'Sentiment'
              ? 0
              : getPercentageValue(value, infoMap.mentions || 0, 6);
          stacked[key].games.push({ ...game, value, rate: rate });
        });
        let itemInfo = {
          ...game,
          mentions: infoMap.mentions,
          sentiment: infoMap.avg_sentiment,
        };
        let gameDetail = xAxisList.find(
          (v: any) => v.edition_unified_id === game.edition_unified_id,
        );
        itemInfo.name = gameDetail?.entity_name;
        itemInfo.cover = gameDetail?.cover;
        matrixDiagram.push(itemInfo);
      });
      setScatterChartList(matrixDiagram);
      let newData = Object.values(stacked);
      const sortList =
        xAxisList?.map((item: any) => item?.edition_unified_id) || [];
      newData.forEach((item: any) => {
        item?.games?.sort(
          (a: any, b: any) =>
            sortList.indexOf(a?.edition_unified_id) -
            sortList.indexOf(b?.edition_unified_id),
        );
      });
      setMentionsList(newData);
    } catch (error) {
      setMentionsList([]);
      setScatterChartList([]);
    } finally {
      setMentionsLoading(false);
    }
  };

  useEffect(() => {
    let { edition_unified_id } = commonParams || {};
    if (edition_unified_id) {
      getKeyMetricsData();
    }
  }, [commonParams]);

  return (
    <>
      <Col span={12}>
        <CardCommon
          title={formatMessage({ id: 'Mentions & Sentiment' })}
          defaultOperation={chartOperation}
          loading={mentionsLoading}
          loadingHeight={340}
          bodyStyle={{ height: 428 }}
          handleOperationsOnClick={(item: any) => {
            setChartOperation(item.symbol);
          }}
          operations={[barIcon, coordinateIcon]}
        >
          <>
            {chartOperation === 'bar' ? (
              <BarAndLineChart
                data={mentionsList}
                xAxisList={xAxisList}
                height={324}
                commonParams={commonParams}
              ></BarAndLineChart>
            ) : (
              <CoordinateChart
                scatterData={scatterChartList}
                filterParams={commonParams}
                height={332}
              ></CoordinateChart>
            )}
          </>
        </CardCommon>
      </Col>
      <Col span={12}>
        <CardCommon
          title={formatMessage({ id: 'Account Posts & Engagement' })}
          loading={loading}
          loadingHeight={300}
          filterClassName="mentions-sentiment-tab"
          defaultOperation={postsOperation}
          handleOperationsOnClick={(item: any) => {
            setPostsOperation(item.symbol);
          }}
          handleFilterOnChange={(value: string) => {
            setFilterValue(value);
          }}
          operations={[barIcon, lineIcon]}
          filters={formatOptionsMessage([
            { text: 'Account Posts', value: 'account_posts' },
            { text: 'Engagement', value: 'engagement' },
          ])}
        >
          {postsOperation === 'bar' ? (
            <BarChart
              xAxisList={xAxisList}
              data={chartData}
              chartType={postsOperation}
              filter={commonParams}
              showName={false}
              title={
                filterValue === 'account_posts' ? 'Account Posts' : 'Engagement'
              }
            ></BarChart>
          ) : (
            <LineChart
              xAxisList={xAxisList}
              data={chartData}
              title={
                filterValue === 'account_posts' ? 'Account Posts' : 'Engagement'
              }
              filter={commonParams}
            ></LineChart>
          )}
        </CardCommon>
      </Col>
    </>
  );
}
